<template lang='pug'>
#customer-reviews(v-element-visibility="onElementVisibility")
  .reviews-overview-wrapper
    h2.reviews-head {{__('Reviews & Ratings')}}
    .reviews-overview
      .average(v-if="average") {{ average }}
      Stars.rating(v-if="average" :rating="average")
      .total(v-if="total") {{ __('{total} Reviews', { total }) }}
      RogueButton.write(color="gray" size="m" @click="writeReview()") {{__('Write a Review')}}
  hr
  .review-summary(v-if="showReviewSummary" :class="{'summary-collapsed': summaryCollapsed}")
    h3.review-summary-header {{ __('Summary of Reviews') }}
    p {{reviewSummary}}
    button.toggle-more(@click="summaryCollapsed = !summaryCollapsed" ) {{ summaryCollapsed ? __('View more') : __('View less') }}
  hr(v-if="showReviewSummary")
  ReviewGallery.review-photos(v-if="showReviewGallery" :sku="product.sku")
  .count(v-if="total")
    .num
      | {{__('{countStart} - {countEnd} of {total} Reviews', {countStart: offset + 1, countEnd: offset + visibleReviews.length, total: total })}}
    RogueSelect#reviews-sort(label="Sort by" :options="sortOptions" v-model="sort" @update:modelValue="changeSort")
  hr(v-if="total")
  .reviews
    .no-reviews(v-if="loaded && visibleReviews.length === 0 && offset === 0")
      button.link(@click="writeReview()") {{ __('Be the first to review this product') }}
    Review(v-for="review in visibleReviews" :key="review.id" :review="review")
    .pagination(v-if="total")
      .num
        | {{__('{countStart} - {countEnd} of {total} Reviews', {countStart: offset + 1, countEnd: offset + visibleReviews.length, total: total })}}
      .btns(v-if="offset > 0 || visibleReviews.length >= limit")
        button.a11y.page-btn(:class="{disabled: offset === 0}" @click.stop.prevent="setPage(-1)" aria-label="Prev")
          Icon(name="arrow-left")
        button.a11y.page-btn(:class="{disabled: visibleReviews.length < limit || loading}" @click.stop.prevent="setPage(1)" aria-label="Next")
          Icon(name="arrow-right")
</template>

<script setup lang="ts">
import type { IProduct } from '@rogue/universal'
import { computed, defineAsyncComponent, onBeforeUnmount, onServerPrefetch, ref, useTemplateRef, watch } from 'vue'
import { vElementVisibility } from '@vueuse/components'
import Review from './review.vue'
import { useReviewsStore } from '@/js/stores/reviewsStore'
import { closeReviewSubmission, openReviewSubmission } from '@/js/services/reviews/submission'
import type { Review as VisibleReview } from '@/js/services/reviews/reviewTypes'
import { useRogue } from '@/js/services/Rogue'

const ReviewGallery = defineAsyncComponent(() => import('./review-gallery.vue'))

const props = defineProps<{
  product: IProduct,
}>()

const reviewsStore = useReviewsStore()
const rogue = useRogue()

const sortOptions = [
  { label: 'Featured', value: 'featured' },
  { label: 'Most Relevant', value: 'relevant' },
  { label: 'Most Helpful', value: 'helpful' },
  { label: 'Highest to Lowest Rating', value: 'highToLow' },
  { label: 'Most Recent', value: 'recent' },
]

const loaded = ref(false)
const loading = ref(false)
const componentIsVisible = ref(false)
const offset = ref(0)
const limit = ref(8)
const sort = ref('featured')
const visibleReviews = ref<VisibleReview[]>([])
// used to track if we need show more/show less on mobile views
const summaryCollapsed = ref<boolean>(true)

const headerRef = useTemplateRef<HTMLHeadingElement>('header')

const reviews = computed(() => reviewsStore.reviews)
const summaries = computed(() => reviewsStore.summaries)
const total = computed<number>(() => summaries.value[props.product.sku.toUpperCase()]?.count || 0)
const average = computed<number | undefined>(() => summaries.value[props.product.sku.toUpperCase()]?.average)
const reviewSummary = computed<string | undefined>(() => reviewsStore.reviewSummaries[props.product.sku.toUpperCase()])
const showReviewSummary = computed<boolean>(() => !!reviewSummary.value && props.product.attributes?.show_reviews_summary)
const hasPhotos = computed<boolean>(() => (reviewsStore.gallery[props.product.sku.toUpperCase()] || []).totalReviewsWithPhotos > 0)
const showReviewGallery = computed(() => hasPhotos.value && props.product.attributes?.show_reviews_gallery)

let openedReview = false

async function writeReview() {
  openedReview = true
  await openReviewSubmission(props.product.sku, rogue)
}

async function load() {
  loading.value = true
  // The first eight reviews are stored in the Reviews store, so they can be passed from SSR to SPA
  if (offset.value === 0 && sort.value === 'featured' && limit.value === 8) {
    // Loading first reviews...
    if (!reviews.value[props.product.sku.toUpperCase()]) {
      await reviewsStore.loadFirstEightReviews(props.product.sku, rogue)
    }
    visibleReviews.value = reviews.value[props.product.sku.toUpperCase()] || []
  } else {
    // If we are looking at something else, we need to load it directly from the Bazaarvoice backend
    visibleReviews.value = await import(/* webpackChunkName: "reviews" */'@/js/services/reviews/bazaarvoice')
      .then(fns => fns.getReviews(props.product.sku, rogue, offset.value, limit.value, sort.value, false, false))
      .then(result => result.reviews || [])
  }

  // Summaries are stored separately from reviews
  if (!summaries.value[props.product.sku.toUpperCase()]) {
    await reviewsStore.loadSummaries([props.product.sku], rogue)
  }

  loading.value = false
  loaded.value = true
}

async function setPage(dir: 1 | -1) {
  if (offset.value === 8 && dir === -1) {
    offset.value = 0
    limit.value = 8
  } else {
    offset.value += (dir * limit.value)
    limit.value = 30
  }

  rogue.track.track({ type: 'custom', event: 'reviewsPage', data: { page: (offset.value / limit.value) } })

  await load()
  if (headerRef.value) {
    headerRef.value.scrollIntoView({ block: 'start', behavior: 'smooth' })
  }
}

function changeSort() {
  offset.value = 0
  limit.value = 8
  rogue.track.track({ type: 'custom', event: 'changeReviewsSort', data: { sortOrder: sort.value } })
  load()
}

function onElementVisibility(isVisible: boolean) {
  if (!componentIsVisible.value && isVisible) {
    componentIsVisible.value = true
    rogue.track.track({ type: 'custom', event: 'reviewsVisible', data: { sku: props.product.sku } })
  }
}

watch(() => props.product.sku, () => {
  visibleReviews.value = []
  loaded.value = false
  load()
})

onServerPrefetch(async () => {
  try {
    await load()
  } catch (e) {
    rogue.log.warn('Error rendering reviews', e)
  }
})

onBeforeUnmount(() => {
  if (openedReview) {
    closeReviewSubmission()
  }
})

if (!rogue.ssr) {
  load()
}
</script>
<style lang="scss" scoped>
#customer-reviews {
  position: relative;
  scroll-margin-top: $space-m;

  hr {
    border-color: $color-gray-light;
    border-top: none;
    margin: $space-s 0;
  }
}

.review-photos {
  margin-bottom: $space-m;
}

.review-summary {
  &-header {
    font-size: $size-m;
    margin-bottom: $space-xs;
  }

  &.summary-collapsed {
      p {
        @include mobile() {
          display: -webkit-box;
          overflow: hidden;
          text-overflow: ellipsis;
          -webkit-box-orient: vertical;
          height: 72px;
          line-clamp: 3;
          -webkit-line-clamp: 3;
        }
    }
  }

  .toggle-more {
    display: none;
    width: auto;
    background-color: inherit;
    color: $color-black-light;
    font-size: $size-s;
    font-weight: 500;
    border: none;
    padding: 0;
    text-decoration: underline;
    margin-top: $space-xxs;
    cursor: pointer;

    @include mobile() {
      display: inline-block;
    }
  }

  .average-rating {
    font-size: $size-xs;
    font-weight: bold;
    margin-bottom: $space-xs;
  }

  p {
    margin: 0;
    font-size: $size-s;
  }
}

.reviews-overview-wrapper {
  position: relative;
}

.reviews-head {
  margin-bottom: $space-s;
  line-height: $space-s;
}

.reviews-overview {
  display: flex;
  flex-direction: row;
  gap: $space-s;
  align-items: center;

  .average {
    font-family: $font-condensed;
    font-size: $space-xxl;
    font-weight: bold;
    line-height: $space-xxl;

    @include mobile() {
      font-size: $size-xxxl;
      line-height: $size-xxxl;
    }
  }

  .rating {
    font-size: $size-xxl;

    @include mobile() {
      font-size: $size-l;
    }
  }

  .total {
    font-size: $size-s;
    line-height: $size-l;
    margin-right: $space-s;

    @include mobile() {
      font-size: $size-xs;
      margin: 0;
    }
  }

  .write {
    padding: $space-xs $space-xl;

    @include mobile() {
      flex-basis: 100%;
    }
  }

  @include mobile() {
    flex-wrap: wrap;
  }
}

.count {
  font-size: $size-m;
  display: flex;
  justify-content: space-between;
  align-items: center;
  text-transform: uppercase;
  font-weight: bold;
  font-family: $font-header;

  .field {
    margin-bottom: 0;

    // Screen-reader-only "Sort by" label; the approved design has no visible label
    :deep(.label) {
      position: absolute;
      width: 1px;
      height: 1px;
      overflow: hidden;
      clip: rect(0 0 0 0);
      white-space: nowrap;
    }
  }
}

.no-reviews {
  margin-bottom: $space-l;
}

.pagination {
  display: flex;
  user-select: none;
  justify-content: space-between;
  font-size: $size-xs;
  margin-bottom: $space-m;

  .btns {
    display: flex;
  }

  .page-btn {
    background-color: $color-gray-light;
    cursor: pointer;
    padding: $space-xxs;
    border-radius: $radius-default;

    &.disabled {
      pointer-events: none;
      opacity: $opacity-disabled;
    }

    &:first-child {
      margin-right: $space-xs;
    }

    &:hover {
      background-color: $color-gray;
    }

    .icon {
      width: 12px;
      height: 12px;
    }
  }
}
</style>